import React from 'react';
import { getTextWithHighlight } from '@intelligence/utils/commonUtils';
import { isEmpty } from '@gp/utils/common';
import {
  GET_TEXT_WITH_HIGHLIGHT_OPTION,
  SELECT_PANEL_HEIGHT,
  SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX,
} from '@gp/constants/const';

import type { CheckChangeContext } from 'tea-component';
import type { IFormControlType } from '@gp/types/global';

/**
 * 获取表单字段控件placeholder（通用）
 * @param type 表单字段控件类型
 * @param label 表单字段名称
 */
export const getPlaceholder = (type: IFormControlType, label?: string): string => {
  let placeholder = '';
  switch (type) {
    case 'input':
    case 'textarea': {
      placeholder = label ? `Please input ${label}` : 'Please input';
      break;
    }
    case 'select':
    case 'selectMultiple': {
      placeholder = label ? `Please select ${label}` : 'Please select';
      break;
    }
  }
  return placeholder;
};

/**
 * 获取表单字段控件搜索的placeholder（通用）
 * @param label 表单字段名称
 */
export const getSearchPlaceholder = (label?: string): string => (label ? `Search any ${label}` : 'Search');

export const getSelectPanelHeight = (searchable?: boolean, listHeight?: any): number => {
  if (!isEmpty(listHeight)) return listHeight;
  return searchable ? SELECT_PANEL_HEIGHT : SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX;
};

export const getOptionPureText = (option?: IGPSelectOptionWithGroup | null): string => {
  if (!option) return '';
  const { label, text } = option;
  if (typeof label === 'string' && !!label) return label;
  if (typeof text === 'string') return text;
  return '';
};

export const getOptionsWithLabel = (options?: IGPSelectOptionWithGroup[] | null): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(options) || !options.length) return [];
  return options.map((option) => {
    return { ...option, label: getOptionPureText(option) };
  });
};

export const getOptionsWithHighlightSearchText = (
  options?: IGPSelectOptionWithGroup[] | null,
  searchable?: boolean,
  searchValue?: string,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(options) || !options.length) return [];
  if (!searchable) return options;
  if (!searchValue?.trim()) return options;
  return options.map((option) => {
    const pureText = getOptionPureText(option);
    const textTmp = getTextWithHighlight(pureText || '', searchValue, GET_TEXT_WITH_HIGHLIGHT_OPTION);
    return { ...option, text: <span title={pureText}>{textTmp}</span> };
  });
};

/**
 * tea组件的Checkbox.Group组件勾选时，把最新勾选的排在最前面
 * @param payload Checkbox.Group组件onChange回调的第一个参数，即最新勾选数据
 * @param context Checkbox.Group组件onChange回调的第二个参数
 * @return 处理后的最新勾选数据，最新勾选的排在最前面
 */
export const reverseCheckboxGroupValueWhenCheckChange = (
  payload: string[],
  context: CheckChangeContext | null,
): string[] => {
  if (!Array.isArray(payload) || !payload.length) return [];
  const { name = '', value = false } = context?.check || {};
  if (!value) return payload;
  const prevCheckedValue = payload.filter((item) => item !== name);
  return [name, ...prevCheckedValue];
};
